import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import { useUIStore } from '@/store/uiStore';
import styles from './ConfirmDialog.module.css';

export function ConfirmDialog() {
  const { t } = useTranslation();
  const confirmRequest = useUIStore((s) => s.confirmRequest);
  const resolveConfirm = useUIStore((s) => s.resolveConfirm);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const open = confirmRequest !== null;
  const options = confirmRequest?.options;

  // Auto-focus cancel button for destructive dialogs (safety)
  useEffect(() => {
    if (open && options?.danger) {
      const timer = setTimeout(() => cancelRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open, options?.danger]);

  function handleConfirm() {
    resolveConfirm(true);
  }

  function handleCancel() {
    resolveConfirm(false);
  }

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      size="sm"
      closeOnBackdrop
    >
      {options && (
        <>
          <div className={styles.header}>
            <i
              className={`fa-solid ${options.danger ? 'fa-triangle-exclamation' : 'fa-circle-question'} ${styles.icon} ${options.danger ? styles.danger : styles.info}`}
              aria-hidden="true"
            />
            <h2 className={styles.title}>{options.title}</h2>
          </div>

          {options.message && (
            <p className={styles.message}>{options.message}</p>
          )}

          {options.entityInfo && Object.keys(options.entityInfo).length > 0 && (
            <div className={styles.entityInfo}>
              {Object.entries(options.entityInfo).map(([label, value]) => (
                <div key={label} className={styles.entityRow}>
                  <span className={styles.entityLabel}>{label}</span>
                  <span className={styles.entityValue}>{value}</span>
                </div>
              ))}
            </div>
          )}

          <div className={styles.footer}>
            <Button
              ref={cancelRef}
              variant="secondary"
              size="sm"
              onClick={handleCancel}
            >
              {options.cancelText ?? t('btn.cancel')}
            </Button>
            <Button
              variant={options.danger ? 'danger' : 'primary'}
              size="sm"
              onClick={handleConfirm}
            >
              {options.confirmText ?? t('btn.confirm')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
